import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth';
import { Toaster } from 'sonner';
import AppLayout from '@/components/layout/AppLayout';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import EmployeeListPage from '@/pages/EmployeeListPage';
import EmployeeFormPage from '@/pages/EmployeeFormPage';
import EmployeeProfilePage from '@/pages/EmployeeProfilePage';
import AttendancePage from '@/pages/AttendancePage';
import LeavePage from '@/pages/LeavePage';
import ShiftHolidayPage from '@/pages/ShiftHolidayPage';
import PayrollPage from '@/pages/PayrollPage';
import ReportsPage from '@/pages/ReportsPage';
import AuditTrailPage from '@/pages/AuditTrailPage';
import EmployeePortalPage from '@/pages/EmployeePortalPage';
import HierarchyPage from '@/pages/HierarchyPage';
import AdvancesPage from '@/pages/AdvancesPage';
import BonusPage from '@/pages/BonusPage';
import AttendanceReportPage from '@/pages/AttendanceReportPage';
import PolicyPage from '@/pages/PolicyPage';
import { getDefaultRoute, canShowHoliday } from '@/lib/roles';
import '@/App.css';

function ProtectedRoute({ children, requiredRole, requireHoliday }) {
  const { user, loading, hasAccess } = useAuth();
  const defaultRoute = getDefaultRoute(user?.role);
  
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[#0047AB] border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (requiredRole && !hasAccess(requiredRole)) return <Navigate to={defaultRoute} replace />;
  if (requireHoliday && !canShowHoliday(user)) return <Navigate to={defaultRoute} replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  const defaultRoute = getDefaultRoute(user?.role);

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={defaultRoute} replace /> : <LoginPage />} />
      <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to={defaultRoute} replace />} />
        <Route path="dashboard" element={<ProtectedRoute requiredRole="Admin"><DashboardPage /></ProtectedRoute>} />
        <Route path="my-portal" element={<EmployeePortalPage />} />
        <Route path="employees" element={<ProtectedRoute requiredRole="Manager"><EmployeeListPage /></ProtectedRoute>} />
        <Route path="employees/new" element={<ProtectedRoute requiredRole="HR"><EmployeeFormPage /></ProtectedRoute>} />
        <Route path="employees/edit/:id" element={<ProtectedRoute requiredRole="Manager"><EmployeeFormPage /></ProtectedRoute>} />
        <Route path="employees/:id" element={<ProtectedRoute requiredRole="Manager"><EmployeeProfilePage /></ProtectedRoute>} />
        <Route path="attendance" element={<AttendancePage />} />
        <Route path="attendance-report" element={<ProtectedRoute requiredRole="Manager"><AttendanceReportPage /></ProtectedRoute>} />
        <Route path="leaves" element={<LeavePage />} />
        <Route path="shifts-holidays" element={<ProtectedRoute requireHoliday><ShiftHolidayPage /></ProtectedRoute>} />
        <Route path="payroll" element={<ProtectedRoute requiredRole="Manager"><PayrollPage /></ProtectedRoute>} />
        <Route path="reports" element={<ProtectedRoute requiredRole="Manager"><ReportsPage /></ProtectedRoute>} />
        <Route path="audit-trail" element={<ProtectedRoute requiredRole="Admin"><AuditTrailPage /></ProtectedRoute>} />
        <Route path="hierarchy" element={<ProtectedRoute requiredRole="Admin"><HierarchyPage /></ProtectedRoute>} />
        <Route path="advances" element={<ProtectedRoute requiredRole="Manager"><AdvancesPage /></ProtectedRoute>} />
        <Route path="bonus" element={<ProtectedRoute requiredRole="Manager"><BonusPage /></ProtectedRoute>} />
        <Route path="policies" element={<ProtectedRoute requiredRole="HR"><PolicyPage /></ProtectedRoute>} />
      </Route>
      <Route path="*" element={<Navigate to={defaultRoute} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton theme="light" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
